import {CurrencyConstants} from "finhelper";
import {createReducer} from 'redux-act'

import * as actions from '#app/store/actions/interface'
import type {ApiListResponse, ApplicationCreateActionPayload, ContactByIdActionPayload, PortfolioSetActionPayload} from '#app/store/actions/interface'

const readJSON = <T,>(key: string, fallback: T): T => {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
};

export interface InterfaceState {
    title: string;
    loader: boolean;
    clients: any[];
    client: any;
    clientsLoading: boolean;
    currency: any;
    currencyLoading: boolean;
    currencies: any[];
    accounts: any[];
    accountLoading: boolean;
    courses: any[];
    coursesLoading: boolean;
    contacts: any[];
    contactsLoading: boolean;
    applications: any[];
    portfolios: any[];
    selectedPortfolio: any;
    applicationsLoading: boolean;
    sidebarOpen: boolean;

    groupTypes: any[];
    groupTypesLoading: boolean;
    classTypes: any[];
    classTypesLoading: boolean;
    investTypes: any[];
    investTypesLoading: boolean;
    propertyTypes: any[];
    propertyTypesLoading: boolean;
    incomeTypes: any[];
    incomeTypesLoading: boolean;
    spendTypes: any[];
    spendTypesLoading: boolean;
    incomeTags: any[];
    spendTags: any[];
    investTags: any[];
    tags: any[];
    tagsLoading: boolean;
    incomeSubcategories: any[];
    spendSubcategories: any[];
    subcategoriesLoading: boolean;

    callToContact: any;
    selectedApplication?: any;

    [key: string]: any;
}

const state: InterfaceState = {
    title: '',
    loader: false,
    clients: [],
    client: null,
    clientsLoading: true,
    currency: null,
    currencyLoading: true,
    currencies: [],
    accounts: [],
    accountLoading: true,
    courses: [],
    coursesLoading: true,
    contacts: [],
    contactsLoading: true,
    applications: [],
    portfolios: readJSON('portfolios', []),
    selectedPortfolio: readJSON('selectedPortfolio', null),
    applicationsLoading: true,
    sidebarOpen: readJSON('sidebar', true),

    groupTypes: [],
    groupTypesLoading: true,
    classTypes: [],
    classTypesLoading: true,
    investTypes: [],
    investTypesLoading: true,
    propertyTypes: [],
    propertyTypesLoading: true,
    incomeTypes: [],
    incomeTypesLoading: true,
    spendTypes: [],
    spendTypesLoading: true,
    incomeTags: [],
    spendTags: [],
    investTags: [],
    tags: [],
    tagsLoading: true,
    incomeSubcategories: [],
    spendSubcategories: [],
    subcategoriesLoading: true,

    callToContact: null
};

const handleTitle = (state: InterfaceState, title: string): InterfaceState => {
    document.title = title;
    return {
        ...state,
        title: title
    };
}

const handleLoader = (state: InterfaceState, bool: boolean): InterfaceState => {
    return {
        ...state,
        loader: bool
    };
}

const handleClient = (state: InterfaceState, client: any): InterfaceState => {
    localStorage.setItem('client', JSON.stringify(client))

    return {
        ...state,
        client: client,
        clientsLoading: false
    };
}

const handleCurrency = (state: InterfaceState, currency: any): InterfaceState => {
    localStorage.setItem('currency', JSON.stringify(currency))

    return {
        ...state,
        currency: currency,
        currencyLoading: false
    };
}

const handleContacts = (state: InterfaceState, {data}: ApiListResponse): InterfaceState => {
    return {
        ...state,
        contacts: data,
        contactsLoading: false
    }
}

const handleContactById = (state: InterfaceState, {id, data}: ContactByIdActionPayload): InterfaceState => {
    const contactExists = state.contacts.some((item: any) => item.id === id);

    const updatedContacts = contactExists
        ? data
            ? state.contacts.map((contactItem: any) => (contactItem.id === id ? {...contactItem, ...data} : contactItem))
            : state.contacts.filter((contactItem: any) => contactItem.id !== id)
        : data
            ? [...state.contacts, {id, ...data}]
            : state.contacts;
    return {...state, contacts: updatedContacts, contactsLoading: false};
};

const handleApplications = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    return {
        ...state,
        applications: response.data,
        applicationsLoading: false
    }
}

const handleApplicationCreate = (state: InterfaceState, {id, data}: ApplicationCreateActionPayload): InterfaceState => {
    const applicationExists = state.applications.some((item: any) => item.id === id);

    const updatedApplications = applicationExists
        ? data
            ? state.applications.map((applicationItem: any) => applicationItem.id === id ? {...applicationItem, ...data} : applicationItem)
            : state.applications.filter((applicationItem: any) => applicationItem.id !== id)
        : data
            ? [...state.applications, {id, ...data}]
            : state.applications;
    return {...state, applications: updatedApplications, applicationsLoading: false}
};

const handleClients = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    return {
        ...state,
        clients: response.data,
        clientsLoading: false
    }
}

const updateClientInStore = (state: InterfaceState, updatedClient: any): InterfaceState => {
    const clients = state.clients || [];
    const clientIndex = clients.findIndex((client: any) => client.id === updatedClient.id);

    if (clientIndex !== -1) {
        const updatedClients = [...clients];
        updatedClients[clientIndex] = { ...updatedClients[clientIndex], ...updatedClient };
        return {
            ...state,
            clients: updatedClients
        };
    } else {
        return {
            ...state,
            clients: [...clients, updatedClient]
        };
    }
}

const syncClientsFromAPI = (state: InterfaceState, apiClients: any[]): InterfaceState => {
    const currentClients = state.clients || [];
    const apiClientsMap = new Map(apiClients.map((client: any) => [client.id, client]));

    const syncedClients = currentClients.map((client: any) => {
        const apiClient = apiClientsMap.get(client.id);
        return apiClient ? { ...client, ...apiClient } : client;
    });

    const newClients = apiClients.filter((apiClient: any) =>
        !currentClients.some((client: any) => client.id === apiClient.id)
    );

    return {
        ...state,
        clients: [...syncedClients, ...newClients],
        clientsLoading: false
    };
}

const handleClearClient = (state: InterfaceState, _client: any): InterfaceState => {
    localStorage.removeItem('client')

    return {
        ...state,
        client: null,
    };
}

const handleCurrencies = (state: InterfaceState, data: any[]): InterfaceState => {
    const currency = readJSON('currency', data[0] ? data[0] : null);

    CurrencyConstants.currencies = data;

    return {
        ...state,
        currency: currency,
        currencyLoading: currency ? false : true,
        currencies: data
    }
}


const handleLogoutInterface = (_state: InterfaceState): InterfaceState => {

    return {
        title: '',
        loader: false,
        clients: [],
        client: null,
        clientsLoading: true,
        currency: null,
        currencyLoading: true,
        currencies: [],
        accounts: [],
        accountLoading: true,
        courses: [],
        coursesLoading: true,
        contacts: [],
        contactsLoading: true,
        applications: [],
        portfolios: readJSON('portfolios', []),
        selectedPortfolio: readJSON('selectedPortfolio', null),
        applicationsLoading: true,
        sidebarOpen: readJSON('sidebar', true),

        groupTypes: [],
        groupTypesLoading: true,
        classTypes: [],
        classTypesLoading: true,
        investTypes: [],
        investTypesLoading: true,
        propertyTypes: [],
        propertyTypesLoading: true,
        incomeTypes: [],
        incomeTypesLoading: true,
        spendTypes: [],
        spendTypesLoading: true,
        incomeTags: [],
        spendTags: [],
        investTags: [],
        tags: [],
        tagsLoading: true,
        incomeSubcategories: [],
        spendSubcategories: [],
        subcategoriesLoading: true,

        callToContact: null
    }
}

const handleCourses = (state: InterfaceState, data: any[]): InterfaceState => {
    CurrencyConstants.courses = data;

    return {
        ...state,
        courses: data,
        coursesLoading: false
    }
}

const handleSpendTypes = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    if (typeof response === 'object' && response.result === 'success') {
        return {
            ...state,
            spendTypes: response.data,
            spendTypesLoading: false
        }
    } else {
        return state;
    }
}

const handleIncomeTypes = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    if (typeof response === 'object' && response.result === 'success') {
        return {
            ...state,
            incomeTypes: response.data,
            incomeTypesLoading: false
        }
    } else {
        return state;
    }
}

const handleSidebarToggle = (state: InterfaceState): InterfaceState => {
    localStorage.setItem('sidebar', JSON.stringify(!state.sidebarOpen))

    return {
        ...state,
        sidebarOpen: !state.sidebarOpen
    }
}

const handleSidebarOpen = (state: InterfaceState): InterfaceState => {
    localStorage.setItem('sidebar', JSON.stringify(true))

    return {
        ...state,
        sidebarOpen: true
    }
}

const handlePropertyTypes = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    if (typeof response === 'object' && response.result === 'success') {
        return {
            ...state,
            propertyTypes: response.data,
            propertyTypesLoading: false
        }
    } else {
        return state;
    }
}

const handleInvestTypes = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    if (typeof response === 'object' && response.result === 'success') {
        return {
            ...state,
            investTypes: response.data,
            investTypesLoading: false
        }
    } else {
        return state;
    }
}

const handleGroupTypes = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    if (typeof response === 'object' && response.result === 'success') {
        return {
            ...state,
            groupTypes: response.data,
            groupTypesLoading: false
        }
    } else {
        return state;
    }
}

const handleClassTypes = (state: InterfaceState, response: ApiListResponse): InterfaceState => {
    if (typeof response === 'object' && response.result === 'success') {
        return {
            ...state,
            classTypes: response.data,
            classTypesLoading: false
        }
    } else {
        return state;
    }
}

const handleStartCall = (state: InterfaceState, contact: string): InterfaceState => {

    return {
        ...state,
        callToContact: contact
    }
}

const handleStopCall = (state: InterfaceState): InterfaceState => {

    return {
        ...state,
        callToContact: null
    }
}

const portfolioSetHandler = (state: InterfaceState, response: PortfolioSetActionPayload): InterfaceState => {
    const { portfolios } = state;
    const updatedPortfolios = portfolios.reduce((accumulator: any[], portfolio: any) => {
        if (portfolio.user_id === response.user_id) {
            accumulator.push({ ...portfolio, portfolio_date: response.date });
        } else {
            accumulator.push(portfolio);
        }
        return accumulator;
    }, [] as any[]).concat(
        portfolios.every((portfolioItem: any) => portfolioItem.user_id !== response.user_id)
            ? [{ user_id: response.user_id, portfolio_date: response.date }]
            : []
    );

    try {
        localStorage.setItem('portfolios', JSON.stringify(updatedPortfolios));
    } catch (error) {
        console.error('Ошибка при сохранении portfolios в localStorage:', error);
    }

    return {
        ...state,
        portfolios: updatedPortfolios,
    };
};

const portfolioGetHandler = (state: InterfaceState, action: any): InterfaceState => {
    const user_id = action.payload;
    const portfolio = state.portfolios.find((item: any) => item.user_id === user_id);
    return {
        ...state,
        selectedPortfolio: portfolio || null,
    };
};

const applicationGetHandler = (state: InterfaceState, action: any): InterfaceState => {
    const _application_id = action.payload;
    const application = state.applications.find((item: any) => application.id === item.id);
    return {
        ...state,
        selectedApplication: application ?? null,
    }
}

const applicationSetHandler = (state: InterfaceState, response: any): InterfaceState => {
    const updatedApplications = state.applications
        .map((app: any) => app.id === response.id ? { ...app, ...response } : app)
        .filter(Boolean);

    if (!updatedApplications.some((app: any) => app.id === response.id)) {
        updatedApplications.push(response);
    }

    return {
        ...state,
        applications: [...updatedApplications],
    };
}

const updateApplicationInStore = (state: InterfaceState, updatedApplication: any): InterfaceState => {
    const applications = state.applications || [];
    const applicationIndex = applications.findIndex((application: any) => application.id === updatedApplication.id);

    if (applicationIndex !== -1) {
        const updatedApplications = [...applications];
        updatedApplications[applicationIndex] = { ...updatedApplications[applicationIndex], ...updatedApplication };
        return {
            ...state,
            applications: updatedApplications
        };
    } else {
        return {
            ...state,
            applications: [...applications, updatedApplication]
        };
    }
}

const syncApplicationsFromAPI = (state: InterfaceState, apiApplications: any[]): InterfaceState => {
    const currentApplications = state.applications || [];
    const apiApplicationsMap = new Map(apiApplications.map((application: any) => [application.id, application]));

    const syncedApplications = currentApplications.map((application: any) => {
        const apiApplication = apiApplicationsMap.get(application.id);
        return apiApplication ? { ...application, ...apiApplication } : application;
    });

    const newApplications = apiApplications.filter((apiApplication: any) =>
        !currentApplications.some((application: any) => application.id === apiApplication.id)
    );

    return {
        ...state,
        applications: [...syncedApplications, ...newApplications],
        applicationsLoading: false
    };
}

const reducer = createReducer<InterfaceState>((on) => {
    on(actions.handleTitle, handleTitle);
    on(actions.handleCurrency, handleCurrency);
    on(actions.handleClient, handleClient);
    on(actions.handleClients, handleClients);
    on(actions.handleCurrencies, handleCurrencies);
    on(actions.handleCourses, handleCourses);
    on(actions.handleClearClient, handleClearClient);
    on(actions.handleLoader, handleLoader);
    on(actions.handleSidebarToggle, handleSidebarToggle);
    on(actions.handleSidebarOpen, handleSidebarOpen);
    on(actions.handleSpendTypes, handleSpendTypes);
    on(actions.handleIncomeTypes, handleIncomeTypes);
    on(actions.handlePropertyTypes, handlePropertyTypes);
    on(actions.handleInvestTypes, handleInvestTypes);
    on(actions.handleGroupTypes, handleGroupTypes);
    on(actions.handleClassTypes, handleClassTypes);
    on(actions.handleContacts, handleContacts);
    on(actions.handleContactById, handleContactById);
    on(actions.handleApplications, handleApplications);
    on(actions.handleApplicationCreate, handleApplicationCreate);
    on(actions.handleStartCall, handleStartCall);
    on(actions.handleStopCall, handleStopCall);
    on(actions.handleLogoutInterface, handleLogoutInterface);
    on(actions.handleSetPortfolio, portfolioSetHandler);
    on(actions.handleGetPortfolioById, portfolioGetHandler);
    on(actions.handleGetApplicationById, applicationGetHandler);
    on(actions.handleSetApplicationById, applicationSetHandler);
    on(actions.updateClientInStore, updateClientInStore);
    on(actions.syncClientsFromAPI, syncClientsFromAPI);
    on(actions.updateApplicationInStore, updateApplicationInStore);
    on(actions.syncApplicationsFromAPI, syncApplicationsFromAPI);
}, state)

export default reducer
